import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { useToast, CalendarIcon, ServiceIcon } from '../components/ui'
import { isPersonalMode, modeAwareEndpoint } from '../utils/cockpitMode'
import './Services.css'

// TQ #6932 Refactor Wave 3: G DATA Partner-Cross-Sell (Box-Shots + Logo).
// Wird im Personal-Mode unter der Lizenz-Liste gerendert (PersonalLicensesFallback
// entfernt, Lizenzen kommen jetzt real aus /api/me/licenses).
function GDataCrossSell() {
  return (
    <>
      <div style={{ marginTop: '2.5rem', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.1rem', color: '#374151' }}>Antivirus &amp; Sicherheit</h2>
        <Link to="/gdata" style={{ fontSize: '0.85rem', color: '#dc2626' }}>Alle G DATA-Produkte ansehen →</Link>
      </div>
      <div className="services-grid">
        <div className="service-card" style={{
          background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
          borderColor: '#e2e8f0',
        }}>
          <div className="service-header">
            <img
              src="brand/gdata-internet-security-box.png"
              alt="G DATA Internet Security"
              style={{ width: 72, height: 92, objectFit: 'contain', flexShrink: 0 }}
            />
            <div className="service-info">
              <div className="service-name">G DATA Internet Security</div>
              <div style={{ fontSize: '0.8rem', color: '#475569' }}>IT-SCE ist offizieller G DATA Partner</div>
            </div>
          </div>
          <p style={{ fontSize: '0.9rem', color: '#4b5563', margin: '0.75rem 0 1rem' }}>
            Made in Germany. Schützt deinen PC vor Viren, Ransomware und Phishing.
            Pakete ab 33,57 €/Jahr — der genaue Preis hängt von Anzahl Geräten ab.
          </p>
          <a
            href="mailto:vertrieb@it-sce.de?subject=Angebotsanfrage%20G%20DATA%20Internet%20Security&body=Hallo%20IT-SCE-Team%2C%0A%0Aich%20interessiere%20mich%20f%C3%BCr%20G%20DATA%20Internet%20Security.%0A%0AAnzahl%20Ger%C3%A4te%3A%20%0ALaufzeit%3A%20%0A%0ABitte%20sendet%20mir%20ein%20passendes%20Angebot%20zu.%0A%0ADanke!"
            className="btn-primary" style={{ marginTop: 'auto', textDecoration: 'none' }}
          >Angebot anfragen</a>
        </div>
        <div className="service-card">
          <div className="service-header">
            <img
              src="brand/gdata-cyberdefense-logo.png"
              alt="G DATA CyberDefense"
              style={{ width: 72, height: 72, objectFit: 'contain', flexShrink: 0 }}
            />
            <div className="service-info">
              <div className="service-name">G DATA Geschäftslizenzen</div>
              <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Endpoint Protection für Unternehmen</div>
            </div>
          </div>
          <p style={{ fontSize: '0.9rem', color: '#4b5563', margin: '0.5rem 0 1rem' }}>
            Endpoint Protection für mehrere Mitarbeiter oder ein ganzes Unternehmen?
            Wir setzen das mit dir auf — kostenlose Beratung über den Support.
          </p>
          <Link to="/support" className="btn-primary" style={{ marginTop: 'auto' }}>Support kontaktieren</Link>
        </div>
      </div>
    </>
  )
}

interface Organization {
  id: number
  name: string
  slug: string
  public_token?: string
  member_role: string
}

interface LicenseProduct {
  id: string
  name: string
  slug: string
  description: string
}

interface License {
  id: string
  key: string
  product: LicenseProduct
  license_type: string
  max_seats: number
  active_seats: number
  features: string[]
  starts_at: string | null
  expires_at: string | null
  is_valid: boolean
  status: string
  created_at: string
  // TQ #8003: Diskriminator-Feld. "third-party" = Drittanbieter-Lizenz (z.B. G DATA).
  // Fehlt das Feld (ältere API-Versionen), wird die Lizenz als IT-SCE-Software behandelt.
  category?: 'itsce-software' | 'third-party'
}

interface LicenseCustomerInfo {
  id: string
  email: string
  name: string
  company: string
  is_active: boolean
}

interface LicensesData {
  customer: LicenseCustomerInfo | null
  licenses: License[]
}

interface Props {
  organization: Organization
  /** Unter Services eingebettet (Org-Modus): Abschnitt statt eigener Seite. */
  embedded?: boolean
}

function maskKey(key: string): string {
  // Leerer Key (z.B. ERPNext-Subs für Reseller-Lizenzen wie G DATA — Schlüssel
  // wird per Sales-Invoice-Position vergeben, nicht am Item).
  if (!key) return '—'
  if (key.length <= 8) return '••••••••'
  return key.slice(0, 4) + '••••••••' + key.slice(-4)
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—'
  try {
    return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
  } catch {
    return dateStr
  }
}

function StatusBadge({ status, isValid }: { status: string; isValid: boolean }) {
  const active = isValid && (status === 'active' || status === 'valid')
  const style: React.CSSProperties = {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 600,
    background: active ? '#e6f4ea' : '#fce8ec',
    color: active ? '#1a7340' : '#cc0033',
  }
  return <span style={style}>{active ? 'Aktiv' : status}</span>
}

// TQ #8003: Wiederverwendbarer Tabellen-Renderer für eine License[]-Liste.
// Wird für beide Sektionen (IT-SCE Software + Drittanbieter) genutzt — kein
// doppelter Tabellen-Code, nur die Eingangsdaten unterscheiden sich.
function LicenseTable({ licenses }: { licenses: License[] }) {
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: 10,
      overflow: 'hidden',
      boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
        <thead>
          <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
            <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b7280', fontWeight: 600 }}>Produkt</th>
            <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b7280', fontWeight: 600 }}>Schlüssel</th>
            <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b7280', fontWeight: 600 }}>Sitze</th>
            <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b7280', fontWeight: 600 }}>Status</th>
            <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b7280', fontWeight: 600 }}>Läuft ab</th>
          </tr>
        </thead>
        <tbody>
          {licenses.map((license, idx) => {
            const active = license.is_valid && (license.status === 'active' || license.status === 'valid')
            const name = license.product?.name || '—'
            const initial = name.charAt(0).toUpperCase()
            const isLast = idx === licenses.length - 1
            // Echte Auslastung kennen wir nur wenn active_seats < max_seats (Telemetrie aus License-Platform).
            // Bei ERPNext-Subs ist active_seats == max_seats (kein Telemetrie-Call zum Vendor wie G DATA)
            // → nur die Lizenz-Kapazität zeigen, keine Progress-Bar.
            const hasUsageTelemetry = license.active_seats < license.max_seats
            const seatPct = license.max_seats > 0 ? (license.active_seats / license.max_seats) * 100 : 0
            return (
              <tr
                key={license.id}
                style={{ borderBottom: isLast ? 'none' : '1px solid #f3f4f6' }}
              >
                {/* Produkt — Avatar + Name */}
                <td style={{ padding: '0.875rem 1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{
                      width: 36, height: 36, borderRadius: 8,
                      background: '#eff6ff', color: '#1d4ed8',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: '0.95rem', flexShrink: 0,
                    }}>{initial}</span>
                    <span style={{ fontWeight: 600, color: '#1f2937' }}>{name}</span>
                  </div>
                </td>
                {/* Schlüssel */}
                <td style={{ padding: '0.875rem 1rem' }}>
                  {license.key ? (
                    <code style={{
                      background: '#f3f4f6', padding: '3px 10px', borderRadius: 6,
                      fontFamily: 'monospace', fontSize: '0.8rem', color: '#374151',
                      border: '1px solid #e5e7eb',
                    }}>{maskKey(license.key)}</code>
                  ) : (
                    <span style={{ color: '#9ca3af' }}>—</span>
                  )}
                </td>
                {/* Sitze — bei Telemetrie "X/Y" + Bar, sonst nur "N Sitze" */}
                <td style={{ padding: '0.875rem 1rem', textAlign: 'center' }}>
                  {hasUsageTelemetry ? (
                    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontWeight: 700, color: '#1f2937', fontVariantNumeric: 'tabular-nums' }}>
                        {license.active_seats}<span style={{ color: '#9ca3af', fontWeight: 500 }}> / {license.max_seats}</span>
                      </span>
                      <span style={{
                        display: 'block', width: 72, height: 4, borderRadius: 2,
                        background: '#e5e7eb', overflow: 'hidden',
                      }}>
                        <span style={{
                          display: 'block', height: '100%',
                          width: `${Math.min(seatPct, 100)}%`,
                          background: seatPct > 90 ? '#dc2626' : seatPct > 70 ? '#f59e0b' : '#10b981',
                        }} />
                      </span>
                    </div>
                  ) : (
                    <span style={{ fontWeight: 600, color: '#374151', fontVariantNumeric: 'tabular-nums' }}>
                      {license.max_seats} {license.max_seats === 1 ? 'Sitz' : 'Sitze'}
                    </span>
                  )}
                </td>
                {/* Status */}
                <td style={{ padding: '0.875rem 1rem', textAlign: 'center' }}>
                  <StatusBadge status={license.status} isValid={license.is_valid} />
                </td>
                {/* Läuft ab */}
                <td style={{ padding: '0.875rem 1rem', textAlign: 'right' }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    color: active ? '#374151' : '#dc2626',
                    fontWeight: 500,
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    <CalendarIcon size={14} />
                    {formatDate(license.expires_at)}
                  </span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default function Licenses({ organization, embedded = false }: Props) {
  const { showToast } = useToast()
  const { api: keycloakApi } = useAuth()
  // TQ #6932: Personal-Mode (with.it-sce.de) → /api/me/licenses (schema-identisch,
  // LIVE seit 485fd6e). Org-Mode bleibt auf /api/org/<slug>/licenses.
  const personal = isPersonalMode()
  const [data, setData] = useState<LicensesData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadLicenses()
  }, [personal, organization.slug, organization.public_token])

  const loadLicenses = async () => {
    try {
      setLoading(true)
      const url = modeAwareEndpoint('licenses', organization)
      const response = await keycloakApi!.get(url)
      setData(response.data.data || { customer: null, licenses: [] })
    } catch (error) {
      console.error('Failed to load licenses:', error)
      showToast('error', 'Lizenzen konnten nicht geladen werden')
    } finally {
      setLoading(false)
    }
  }

  if (loading && embedded) return null
  if (loading) {
    return (
      <div style={{ padding: '32px' }}>
        <h2 style={{ marginBottom: '24px' }}>Lizenzen</h2>
        <p style={{ color: '#888' }}>Lizenzen werden geladen…</p>
      </div>
    )
  }

  const licenses = data?.licenses ?? []

  // TQ #8003: Clientseitige Partitionierung nach category-Feld.
  // Fehlendes category-Feld (ältere API) → IT-SCE-Software (backward-compat).
  const itsceLicenses = licenses.filter(l => l.category !== 'third-party')
  const thirdPartyLicenses = licenses.filter(l => l.category === 'third-party')

  const body = (
    <>
      {licenses.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon"><ServiceIcon size={56} color="#ccc" /></div>
          <h2>Keine Lizenzen vorhanden</h2>
          <p>Sobald Lizenzprodukte gebucht oder ausgestellt werden, erscheinen sie hier.</p>
        </div>
      ) : (
        <>
          {/* Sektion 1: IT-SCE Software-Lizenzen (unveränderte Semantik) */}
          {itsceLicenses.length > 0 && (
            <div style={{ marginBottom: thirdPartyLicenses.length > 0 ? '2rem' : 0 }}>
              <h2 style={{ fontSize: '1rem', fontWeight: 600, color: '#374151', marginBottom: '0.75rem' }}>
                IT-SCE Software-Lizenzen
              </h2>
              <LicenseTable licenses={itsceLicenses} />
            </div>
          )}

          {/* Sektion 2: Drittanbieter-Lizenzen — nur rendern wenn vorhanden */}
          {thirdPartyLicenses.length > 0 && (
            <div>
              <h2 style={{ fontSize: '1rem', fontWeight: 600, color: '#374151', marginBottom: '0.75rem' }}>
                Weitere Lizenzen (Drittanbieter)
              </h2>
              <LicenseTable licenses={thirdPartyLicenses} />
            </div>
          )}
        </>
      )}
    </>
  )

  if (embedded) {
    // Services (Org-Modus) enthaelt die Lizenzen als Abschnitt (Spec Informationsarchitektur, Patric 03.10.2026).
    return (
      <section id="lizenzen" style={{ marginTop: '2rem' }}>
        <h2 style={{ fontSize: '1.1rem', color: '#374151', margin: '0 0 0.75rem' }}>
          Lizenzen{licenses.length > 0 ? ` (${licenses.length})` : ''}
        </h2>
        {body}
      </section>
    )
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Lizenzen</h1>
          <p className="page-subtitle">
            {licenses.length === 0
              ? 'Keine Lizenzen vorhanden'
              : `${licenses.length} ${licenses.length === 1 ? 'Lizenz' : 'Lizenzen'}`}
          </p>
        </div>
      </div>

      {body}

      {/* G DATA Partner-Cross-Sell — im Personal-Mode (mit.it-sce.de) wie bisher. */}
      {personal && <GDataCrossSell />}
    </div>
  )
}
